import { analyticsService } from '../index-business';

export interface ClientEventData {
  ownerId?: string;
  clientId?: string;
  studioClientId?: string;
  messageType?: string;
  destinationType?: string;
  [key: string]: any;
}

export class ClientTracker {
  static trackCreateMessageBatch(data: {
    ownerId: string;
    params: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      message_type: data.params?.type, // sms, messenger, email
      destination_type: data.params?.destinationType, // client, pendingClient
      clients_count: data.params?.clientsToMessage?.length || data.params?.clients?.length || 0,
      notify_all: data.params?.notifyAll,
      status: data.params?.status,
      tags: data.params?.tags || [],
      funnel_id: data.params?.funnelId,
      stage: data.params?.stage,
      message_length: data.params?.text?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'createMessageBatch',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Message Batch Created', {
      messageType: eventData.message_type,
      destinationType: eventData.destination_type,
      clientsCount: eventData.clients_count,
      success: data.success,
    });

    analyticsService.track('Message Batch Created', eventData);
  }

  static trackUpdateClientInfo(data: {
    ownerId: string;
    id: string;
    clientInfoData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      client_info_id: data.id,
      info_type: data.clientInfoData?.type,
      info_title: data.clientInfoData?.title,
      info_value: data.clientInfoData?.value ? 'has_value' : 'no_value',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateClientInfo',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Info Updated', {
      infoType: eventData.info_type,
      success: data.success,
    });

    analyticsService.track('Client Info Updated', eventData);
  }

  static trackAddClientInfo(data: {
    ownerId: string;
    studioClientId: string;
    clientInfoData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      studio_client_id: data.studioClientId,
      info_type: data.clientInfoData?.type,
      info_title: data.clientInfoData?.title,
      info_value: data.clientInfoData?.value ? 'has_value' : 'no_value',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addClientInfo',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Info Added', {
      infoType: eventData.info_type,
      success: data.success,
    });

    analyticsService.track('Client Info Added', eventData);
  }

  static trackSendMessage(data: {
    ownerId: string;
    params: any;
    success: boolean;
    error?: string;
    duration?: number;
    endpoint: string; // 'sendMessage', 'sendMessageInMessanger', 'sendEmails'
  }) {
    const eventData = {
      owner_id: data.ownerId,
      message_type: data.params?.type || 'unknown',
      destination_type: data.params?.destinationType,
      recipients_count: data.params?.clients?.length || data.params?.clientsToMessage?.length || 1,
      message_length: data.params?.text?.length || data.params?.message?.length || 0,
      funnel_id: data.params?.funnelId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: data.endpoint,
      timestamp: new Date().toISOString(),
    };

    const eventNames = {
      sendMessage: 'Message Sent',
      sendMessageInMessanger: 'Messenger Message Sent',
      sendEmails: 'Email Sent',
    };

    const eventName = eventNames[data.endpoint as keyof typeof eventNames] || 'Message Sent';

    console.log('📊 Tracking event:', eventName, {
      messageType: eventData.message_type,
      destinationType: eventData.destination_type,
      recipientsCount: eventData.recipients_count,
      success: data.success,
    });

    analyticsService.track(eventName, eventData);
  }

  static trackAddClient(data: { ownerId: string; client: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      owner_id: data.ownerId,
      client_name: data.client?.name,
      client_phone: data.client?.phone ? 'has_phone' : 'no_phone',
      client_email: data.client?.email ? 'has_email' : 'no_email',
      client_tags: data.client?.tags || [],
      client_balance: data.client?.balance || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addClient',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Added', {
      clientName: eventData.client_name,
      hasPhone: eventData.client_phone === 'has_phone',
      hasEmail: eventData.client_email === 'has_email',
      success: data.success,
    });

    analyticsService.track('Client Added', eventData);
  }

  static trackUpdateClient(data: {
    ownerId: string;
    id: string;
    client: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const updatedFields = data.client ? Object.keys(data.client) : [];

    const eventData = {
      owner_id: data.ownerId,
      client_id: data.id,
      updated_fields: updatedFields,
      client_name: data.client?.name,
      client_phone: data.client?.phone ? 'has_phone' : 'no_phone',
      client_email: data.client?.email ? 'has_email' : 'no_email',
      client_tags: data.client?.tags || [],
      client_balance: data.client?.balance,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateClient',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Updated', {
      clientId: data.id,
      updatedFields: updatedFields,
      success: data.success,
    });

    analyticsService.track('Client Updated', eventData);
  }

  static trackDeleteClient(data: {
    ownerId: string;
    client: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      client_id: data.client?._id,
      client_name: data.client?.name,
      client_phone: data.client?.phone ? 'has_phone' : 'no_phone',
      client_email: data.client?.email ? 'has_email' : 'no_email',
      client_tags: data.client?.tags || [],
      client_balance: data.client?.balance || 0,
      delete_type: 'soft_delete',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteClient',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Deleted', {
      clientId: eventData.client_id,
      clientName: eventData.client_name,
      deleteType: 'soft',
      success: data.success,
    });

    analyticsService.track('Client Deleted', eventData);
  }

  static trackDeleteClientForever(data: {
    ownerId: string;
    clientId: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      client_id: data.clientId,
      delete_type: 'permanent_delete',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteClientForever',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Deleted Forever', {
      clientId: data.clientId,
      deleteType: 'permanent',
      success: data.success,
    });

    analyticsService.track('Client Deleted Forever', eventData);
  }

  static trackUnDeleteClient(data: {
    ownerId: string;
    client: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      client_id: data.client?._id,
      client_name: data.client?.name,
      client_phone: data.client?.phone ? 'has_phone' : 'no_phone',
      client_email: data.client?.email ? 'has_email' : 'no_email',
      client_tags: data.client?.tags || [],
      client_balance: data.client?.balance || 0,
      action_type: 'restore_client',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'unDeleteClient',
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Tracking event: Client Restored', {
      clientId: eventData.client_id,
      clientName: eventData.client_name,
      success: data.success,
    });

    analyticsService.track('Client Restored', eventData);
  }

  static trackClientApiCall(
    endpoint: string,
    data: ClientEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Client API: ${endpoint}`, eventData);
  }
}

export const trackClientSuccess = (endpoint: string, data: ClientEventData, duration?: number) => {
  ClientTracker.trackClientApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackClientError = (endpoint: string, error: string, data: ClientEventData, duration?: number) => {
  ClientTracker.trackClientApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default ClientTracker;
